import React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { AsyncDataPlaceholder } from '../../../../../ui';
import { AvailabilityResultsContent } from './AvailabilityResultsContent';
import { ParkingProductDetailsDialogRoute } from './details/ParkingProductDetailsDialogRoute';
import { usePrefetchNextPage } from './helpers/usePrefetchNextPage';
import { useSelectProduct } from './helpers/useSelectProduct';
import { AvailabilityNoResults } from './AvailabilityNoResults';
import { PromoCodeResult } from './PromoCodeResult';
import { useLoadParkingProductsWhenNeeded } from './helpers/useLoadParkingProductsWhenNeeded';
import { useResetParkingProductUpgradeWhenMounted } from './helpers/useResetParkingProductUpgradeWhenMounted';
import { useNewBookingStore } from '../../../domain/booking/stores/useNewBookingsStore';
import { ApiErrorMessage } from '../../errors/ApiErrorMessage';
import { useBookingDateValidation } from '../shared/validation/useBookingDateValidation';
import { useStateValidation } from '../shared/validation/useStateValidation';

export interface IAvailabilityProps {
	previousUrl?: string;
	extrasUrl: string;
	fallbackUrl: string;
}

export const AvailabilityResults = observer(
	({ previousUrl, extrasUrl, fallbackUrl }: IAvailabilityProps) => {
		useStateValidation([useBookingDateValidation()]);

		const { parkingProductsQuoteStore, booking } = useNewBookingStore();
		const { onSelectProduct, hasSelectedProduct } = useSelectProduct({
			extrasUrl,
			fallbackUrl,
		});

		usePrefetchNextPage({ extrasUrl, fallbackUrl });

		useLoadParkingProductsWhenNeeded();

		useResetParkingProductUpgradeWhenMounted();

		if (
			parkingProductsQuoteStore.finishedInitialLoad &&
			!parkingProductsQuoteStore.isLoading &&
			parkingProductsQuoteStore.parkingProducts.length === 0
		) {
			return <AvailabilityNoResults previousUrl={previousUrl} />;
		}

		const { promoCode, isPromoCodeValid } = parkingProductsQuoteStore;

		return (
			<>
				{promoCode && (
					<PromoCodeResult isValid={isPromoCodeValid} promoCode={promoCode} />
				)}
				{parkingProductsQuoteStore.error && (
					<StyledApiErrorMessage error={parkingProductsQuoteStore.error} />
				)}
				<AvailabilityResultsContent
					promoCode={promoCode}
					isPromoCodeValid={isPromoCodeValid}
					loading={parkingProductsQuoteStore.isLoading}
					parkingProducts={parkingProductsQuoteStore.parkingProducts}
					selectedParkingProductId={booking.parkingProduct?.id}
					onSelectProduct={onSelectProduct}
				/>
				{hasSelectedProduct && <AsyncDataPlaceholder />}
				<ParkingProductDetailsDialogRoute onSelectProduct={onSelectProduct} />
			</>
		);
	},
);

const StyledApiErrorMessage = styled(ApiErrorMessage)`
	margin-bottom: 24px;
	margin-top: 0;
`;
